import { useEffect, useRef, useState } from 'react';
import { useContracts } from './useContracts';

// Live cutoff countdown. Fetches getCutoffInfo() once, then ticks locally
// every second. When the countdown hits 0 (a new cutoff has landed) it
// refetches automatically so the timer resets to the next window.
export const useCutoffInfo = () => {
  const c = useContracts();
  const [info, setInfo] = useState(null); // { currentCutoffIndex, cutoffAnchor, cutoffPeriod, nextCutoffAt, secondsToNextCutoff }
  const [nowTs, setNowTs] = useState(Math.floor(Date.now() / 1000));
  const refetchingRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const ci = await c.read.views.getCutoffInfo();
        if (mounted) setInfo(ci);
      } catch {}
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNowTs(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!info || refetchingRef.current) return;
    if (nowTs >= Number(info.nextCutoffAt)) {
      refetchingRef.current = true;
      c.read.views.getCutoffInfo()
        .then((ci) => { setInfo(ci); refetchingRef.current = false; })
        .catch(() => { refetchingRef.current = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTs, info]);

  const period = info ? Number(info.cutoffPeriod || 0) : 0;
  const nextAt = info ? Number(info.nextCutoffAt || 0) : 0;
  const secondsToNext = Math.max(0, nextAt - nowTs);
  const elapsed = period > 0 ? Math.min(period, period - secondsToNext) : 0;
  const pct = period > 0 ? (elapsed / period) * 100 : 0;

  return {
    info,
    secondsToNext,
    elapsedInPeriod: elapsed,
    period,
    pct,
    currentCutoffIndex: info ? Number(info.currentCutoffIndex) : 0,
  };
};
